/**
 * Catalog Bundle Metaobject Manager
 * Handles bundle initialization and rendering for Catalog collection products
 * Adapted from collection-bundle-metaobject.js for dynamically loaded products
 */

/**
 * Clean option name by removing identifier suffix (e.g., "| EK244M")
 */
function cleanOptionName(optionName) {
    if (!optionName) return optionName;
    return optionName.split('|')[0].trim();
}

/**
 * Convert string to handleized format for property names
 */
function handleize(name) {
    return name
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^\w\-]+/g, '')
        .replace(/\-\-+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
}

/**
 * Sort bundle options in predefined order
 */
function sortBundleOptions(options) {
    const orderPriority = {
        'piston set': 1,
        'piston ring set': 2,
        'main bearing set': 3,
        'main bearings set': 3,
        'rod bearing set': 4
    };
    
    return options.slice().sort((a, b) => {
        const nameA = cleanOptionName(a.bundle_option_name || '').toLowerCase();
        const nameB = cleanOptionName(b.bundle_option_name || '').toLowerCase();
        
        let priorityA = 999;
        let priorityB = 999;
        
        for (const [key, priority] of Object.entries(orderPriority)) {
            if (nameA.includes(key)) priorityA = Math.min(priorityA, priority);
            if (nameB.includes(key)) priorityB = Math.min(priorityB, priority);
        }
        
        return priorityA - priorityB;
    });
}

window.catalogBundleManager = {
    initialized: new Set(),
    bundleCache: new Map(),

    /**
     * Initialize bundle products in catalog container
     * @param {HTMLElement} container - Container element with product items
     */
    async initializeCatalogProducts(container) {
        // Look for product items with attr-prod-id attribute
        const productItems = container.querySelectorAll('[attr-prod-id]');
        
        
        for (const item of productItems) {
            const productId = item.getAttribute("attr-prod-id");
            const bundleData = window.catalogBundleData?.[productId];
            
            // Check if this product has metaobject bundle data
            if (bundleData && !this.initialized.has(productId)) {
                await this.initializeBundleProduct(item, productId);
                this.initialized.add(productId);
            } else if (bundleData && this.initialized.has(productId)) {
                // Already initialized, but ensure visibility is correct
                const form = item.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");
                if (form) {
                    const bundleOptions = form.querySelector(".bold_option_set");
                    const bundleChooseBtn = item.querySelector("#bundle-choose");
                    const addToCartBtn = item.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
                    
                    if (bundleOptions) {
                        bundleOptions.classList.add("hide-bundle-choose");
                        
                        if (bundleChooseBtn) {
                            bundleChooseBtn.classList.remove("hide-bundle-choose");
                            bundleChooseBtn.style.display = '';
                        }
                        
                        if (addToCartBtn) {
                            addToCartBtn.classList.add("hide-bundle-choose");
                        }
                    }
                }
            } else {
                // No metaobject bundle - remove YMM bundle HTML if it exists
                this.removeYMMBundles(item, productId);
            }
        }
    },

    /**
     * Remove YMM API bundle HTML from products without metaobject bundles
     */
    removeYMMBundles(productItemElement, productId) {
        const form = productItemElement.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");
        if (!form) return;
        
        // Remove YMM bundle options (only if not a metaobject bundle)
        const bundleOptions = form.querySelector(".bold_option_set");
        if (bundleOptions && !bundleOptions.querySelector('.bundle_option_select')) {
            // This is a YMM bundle, not metaobject - remove it
            bundleOptions.remove();
        }
        
        // Remove "Choose Size" button (YMM bundle button)
        const bundleChooseBtn = productItemElement.querySelector("#bundle-choose");
        if (bundleChooseBtn && !productItemElement.classList.contains('product-bundle')) {
            bundleChooseBtn.remove();
        }
        
        // Ensure Add to Cart button is visible and remove bundle-product class
        // This allows standard add to cart to work for products without Metaobject bundle data
        const addToCartBtn = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
        if (addToCartBtn) {
            addToCartBtn.classList.remove("hide-bundle-choose");
            addToCartBtn.classList.remove("bundle-product"); // Remove bundle-product class for standard add to cart
            addToCartBtn.removeAttribute("disabled");
        }
    },

    /**
     * Initialize bundle for a single product
     * @param {HTMLElement} productItemElement - Product item element
     * @param {string} productId - Product ID
     */
    async initializeBundleProduct(productItemElement, productId) {
        const bundleData = window.catalogBundleData?.[productId];
        const form = productItemElement.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");

        if (bundleData && form) {
            this.bundleCache.set(productId, bundleData);
            
            // Add product-bundle class to product item
            productItemElement.classList.add('product-bundle');
            
            // Check if bundle options already exist (from YMM API or previous injection)
            let bundleOptions = form.querySelector(".bold_option_set");
            
            if (!bundleOptions) {
                // Bundle options don't exist - inject them
                this.injectBundleOptions(productItemElement, bundleData, productId);
                bundleOptions = form.querySelector(".bold_option_set");
            } else {
                // Bundle options already exist (likely from YMM API) - update them to work with our system
                this.updateExistingBundleOptions(form, bundleData, productId);
            }
            
            // Ensure proper initial visibility state
            let bundleChooseBtn = productItemElement.querySelector("#bundle-choose");
            const addToCartBtn = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
            
            if (bundleOptions) {
                // Bundle options exist - ensure they're hidden initially
                bundleOptions.classList.add("hide-bundle-choose");
                
                // Show "Choose Size" button if it exists
                if (bundleChooseBtn) {
                    // Update button text to "Choose Size" for metaobject bundles
                    const buttonText = bundleChooseBtn.querySelector('.atc-button--text');
                    if (buttonText) {
                        buttonText.innerHTML = '<i class="fa fa-shopping-cart" aria-hidden="true"></i> Choose Size';
                    } else {
                        bundleChooseBtn.innerHTML = '<span class="atc-button--text"><i class="fa fa-shopping-cart" aria-hidden="true"></i> Choose Size</span>';
                    }
                    bundleChooseBtn.classList.remove("hide-bundle-choose");
                    bundleChooseBtn.removeAttribute("disabled");
                    bundleChooseBtn.style.display = '';
                } else {
                    // Create "Choose Options" button if it doesn't exist
                    this.createChooseButton(productItemElement, form);
                    bundleChooseBtn = productItemElement.querySelector("#bundle-choose");
                }
                
                // Hide "Add to Cart" button initially
                if (addToCartBtn) {
                    addToCartBtn.classList.add("hide-bundle-choose");
                }
            }
            
        } else {
            // No bundle data - ensure buttons are enabled
            const addToCartButton = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
            if (addToCartButton) addToCartButton.removeAttribute("disabled");
            const bundleChooseButton = productItemElement.querySelector("#bundle-choose");
            if (bundleChooseButton) bundleChooseButton.removeAttribute("disabled");
        }
    },

    /**
     * Update existing bundle options (from YMM API) to work with metaobject bundle system
     */
    updateExistingBundleOptions(form, bundleData, productId) {
        if (!bundleData || !bundleData.bundle_options || !bundleData.bundle_options[0]) {
            console.warn(`[Catalog Bundle] No bundle options data for product ${productId}`);
            return;
        }
        
        const optionsData = bundleData.bundle_options[0].options;
        const selects = form.querySelectorAll('.bold_option_set select');
        
        if (selects.length === 0) {
            console.warn(`[Catalog Bundle] No select elements found for product ${productId}`);
            return;
        }
        
        // Create a map of option names to bundle option data (with multiple matching strategies)
        const optionMap = new Map();
        optionsData.forEach((option, index) => {
            const cleanedName = cleanOptionName(option.bundle_option_name);
            // Get original name with SKU suffix if available
            const originalName = option.bundle_option_original_name || option.bundle_option_name;
            // Store with cleaned name as key (case-insensitive)
            optionMap.set(cleanedName.toLowerCase(), { option, index, originalName: originalName, cleanedName });
            // Also store with original name (in case it has SKU suffix)
            if (option.bundle_option_name !== cleanedName) {
                optionMap.set(option.bundle_option_name.toLowerCase(), { option, index, originalName: originalName, cleanedName });
            }
            // Also store key parts for fuzzy matching (e.g., "Piston Ring Set" -> "piston", "ring", "set")
            const nameParts = cleanedName.toLowerCase().split(/\s+/).filter(p => p.length > 2);
            nameParts.forEach(part => {
                if (!optionMap.has(part)) {
                    optionMap.set(part, { option, index, originalName: originalName, cleanedName, isPartial: true });
                }
            });
        });
        
        
        let updatedCount = 0;
        const unmatchedSelects = [];
        
        // Update each select element
        selects.forEach((select, selectIndex) => {
            // Add bundle_option_select class if not present
            if (!select.classList.contains('bundle_option_select')) {
                select.classList.add('bundle_option_select');
            }
            
            // Get option name from attr-name or name attribute
            let optionName = select.getAttribute('attr-name');
            if (!optionName) {
                const nameAttr = select.getAttribute('name');
                if (nameAttr) {
                    optionName = nameAttr.replace('properties[', '').replace(']', '');
                }
            }
            
            if (!optionName) {
                console.warn(`[Catalog Bundle] Could not find option name for select ${selectIndex} in product ${productId}`);
                unmatchedSelects.push(selectIndex);
                return;
            }
            
            const cleanedName = cleanOptionName(optionName);
            // Try multiple matching strategies
            let optionData = optionMap.get(cleanedName.toLowerCase());
            
            // If not found, try matching by partial name (e.g., "Piston Ring Set" matches "Piston Ring Set | EK939M")
            if (!optionData) {
                // Try exact match with different casing
                for (const [key, value] of optionMap.entries()) {
                    if (!value.isPartial && key === cleanedName.toLowerCase()) {
                        optionData = value;
                        break;
                    }
                }
                
                // Try substring matching
                if (!optionData) {
                    for (const [key, value] of optionMap.entries()) {
                        if (!value.isPartial) {
                            const keyLower = key.toLowerCase();
                            const cleanedLower = cleanedName.toLowerCase();
                            if (keyLower.includes(cleanedLower) || cleanedLower.includes(keyLower)) {
                                optionData = value;
                                break;
                            }
                        }
                    }
                }
                
                // Try word-by-word matching
                if (!optionData) {
                    const cleanedWords = cleanedName.toLowerCase().split(/\s+/).filter(w => w.length > 2);
                    for (const [key, value] of optionMap.entries()) {
                        if (!value.isPartial) {
                            const keyWords = value.cleanedName.toLowerCase().split(/\s+/).filter(w => w.length > 2);
                            const matchCount = cleanedWords.filter(w => keyWords.includes(w)).length;
                            if (matchCount >= Math.min(cleanedWords.length, keyWords.length) * 0.7) {
                                optionData = value;
                                break;
                            }
                        }
                    }
                }
            }
            
            if (optionData && optionData.option.options_values) {
                // Update select ID to match our format
                select.id = `dd_select_${productId}_${optionData.index}`;
                
                // Ensure attr-name attribute exists
                if (!select.hasAttribute('attr-name')) {
                    select.setAttribute('attr-name', cleanedName);
                }
                
                // Update data attributes - use original name with SKU suffix if available
                const originalName = optionData.option.bundle_option_original_name || optionData.option.bundle_option_name;
                select.setAttribute('data-original-name', originalName);
                select.setAttribute('data-bundle-option-id', optionData.option.bundle_option_id || '');
                
                // Update option elements with data attributes
                const optionElements = select.querySelectorAll('option');
                let optionUpdated = false;
                
                optionElements.forEach((opt, optIndex) => {
                    if (opt.value && opt.value !== '' && opt.value !== '-- Choose' && !opt.value.includes('-- Choose')) {
                        // Find matching variant in bundle data - try multiple matching strategies
                        let variant = optionData.option.options_values.find(v => {
                            const cleanValue = cleanOptionName(v.option_value);
                            const cleanOptValue = cleanOptionName(opt.value);
                            const optText = opt.textContent.trim();
                            
                            // Exact match (cleaned)
                            if (cleanValue === cleanOptValue) return true;
                            // Exact match (original)
                            if (opt.value === v.option_value) return true;
                            // Text content match
                            if (optText === cleanValue || optText === v.option_value) return true;
                            // Partial match (for cases like "Piston Ring Set Standard Size" vs "Standard Size")
                            if (cleanValue.includes(cleanOptValue) || cleanOptValue.includes(cleanValue)) return true;
                            
                            return false;
                        });
                        
                        if (variant) {
                            opt.setAttribute('data-variant-id', variant.varient_id);
                            opt.setAttribute('data-product-id', variant.product_id);
                            opt.setAttribute('data-price', variant.price || '0.00');
                            opt.setAttribute('data-option_value_key', optIndex);
                            opt.setAttribute('data-original-value', variant.option_value);
                            optionUpdated = true;
                            
                            // Set selected based on visibility and default setting
                            // For visible options, only set default if makeDefault is explicitly set in metaobject
                            // Check if this select is visible (not hidden)
                            const parentDiv = select.closest('.bold_option');
                            let isVisible = true;
                            if (parentDiv) {
                                const style = parentDiv.getAttribute('style') || '';
                                const computedStyle = window.getComputedStyle ? window.getComputedStyle(parentDiv).display : '';
                                isVisible = !style.includes('display: none') && computedStyle !== 'none' && select.style.display !== 'none';
                            }
                            
                            if (variant.is_default == 1) {
                                if (isVisible) {
                                    // Visible options: only set default if explicitly set in metaobject (is_default == 1)
                                    // Since is_default == 1 means it was explicitly set, we can set selected
                                    opt.selected = true;
                                    select.value = opt.value;
                                } else {
                                    // Hidden options can have defaults
                                    opt.selected = true;
                                    select.value = opt.value;
                                }
                            }
                            // If variant.is_default != 1, don't set selected (show placeholder)
                        } else {
                            console.warn(`[Catalog Bundle] No matching variant found for option "${opt.value}" in "${cleanedName}" for product ${productId}`);
                        }
                    }
                });
                
                if (optionUpdated) {
                    updatedCount++;
                } else {
                    console.warn(`[Catalog Bundle] No variants updated for select "${cleanedName}" in product ${productId}`);
                }
            } else {
                console.warn(`[Catalog Bundle] No matching option data found for "${cleanedName}" in product ${productId}. Available options:`, Array.from(optionMap.keys()));
                unmatchedSelects.push(cleanedName);
            }
        });
        
        if (updatedCount > 0) {
        }
        if (unmatchedSelects.length > 0) {
            console.warn(`[Catalog Bundle] Could not match ${unmatchedSelects.length} selects for product ${productId}:`, unmatchedSelects);
        }
        
        // Update hidden fields with initial values
        this.updateBundleHiddenFields(form);
    },

    /**
     * Create "Choose Size" button if it doesn't exist
     */
    createChooseButton(productItemElement, form) {
        const addToCartBtn = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
        if (!addToCartBtn) return;
        
        const chooseBtn = document.createElement('button');
        chooseBtn.type = 'button';
        chooseBtn.id = 'bundle-choose';
        chooseBtn.className = 'btn button-primary productitem--action-trigger';
        chooseBtn.setAttribute('data-bundle-choose-btn', '');
        chooseBtn.innerHTML = '<span class="atc-button--text"><i class="fa fa-shopping-cart" aria-hidden="true"></i> Choose Size</span>';
        
        // Insert before add to cart button
        addToCartBtn.parentNode.insertBefore(chooseBtn, addToCartBtn);
    },

    /**
     * Inject bundle options HTML into product form
     */
    injectBundleOptions(productItemElement, bundleData, parentProductId) {
        let option_set = '<div class="bold_option_set hide-bundle-choose">';
        let optionsHtml = '';
        let name_attributes = [];
        let product_attributes = [];
        let varient_attributes = [];
        let price_attributes = [];
        let qty_attributes = [];
        let shop_product_id = parseInt(parentProductId);

        if (bundleData.bundle_options && bundleData.bundle_options[0] && bundleData.bundle_options[0].options) {
            const options_data = sortBundleOptions(bundleData.bundle_options[0].options);
            
            options_data.forEach((option, b) => {
                const required_type = (option.is_required == 1) ? "required" : "";

                if (option.options_values && option.options_values.length > 1) {
                    // Multiple variants - show as dropdown
                    const cleanedOptionName = cleanOptionName(option.bundle_option_name);
                    const handleizeName = handleize(cleanedOptionName);
                    optionsHtml += `<div class="bold_option bold_option_dropdown ${handleizeName}"><label>`;
                    optionsHtml += `<span class="bold_option_title">${cleanedOptionName}</span>`;
                    // Use original name with SKU suffix for data-original-name
                    const originalOptionName = option.bundle_option_original_name || option.bundle_option_name;
                    optionsHtml += `<span class="bold_option_element"><select id="dd_select_${parentProductId}_${b}" attr-name="${cleanedOptionName}" name="properties[${cleanedOptionName}]" data-original-name="${originalOptionName}" data-bundle-option-id="${option.bundle_option_id || ''}" class="bundle_option_select" ${required_type}>`;
                    optionsHtml += `<option value="">-- Choose ${cleanOptionName(option.bundle_option_name)} (Choose Oversize) --</option>`;

                    // For visible options (multiple variants), only set default if explicitly set in metaobject
                    // Check if this option has makeDefault set (by checking if any variant has is_default == 1)
                    const hasExplicitDefault = option.options_values.some(v => v.is_default == 1);
                    
                    option.options_values.forEach((variant, v) => {
                        if (variant.available !== false) {
                            // Only set selected for visible options if default is explicitly set in metaobject
                            const selected = (variant.is_default == 1 && hasExplicitDefault) ? 'selected' : '';
                            const variantQty = (variant.qty != null && variant.qty !== '') ? String(variant.qty) : '1';
                            optionsHtml += `<option value="${variant.option_value}" data-option_value_key="${v}" data-variant-id="${variant.varient_id}" data-product-id="${variant.product_id}" data-price="${variant.price}" data-qty="${variantQty}" data-original-value="${variant.option_value}" ${selected}>${cleanOptionName(variant.option_value)}</option>`;
                        }
                    });
                    optionsHtml += `</select></span></label></div>`;
                } else if (option.options_values && option.options_values.length === 1) {
                    // Single variant - create dropdown (visible or hidden based on option type)
                    const variant = option.options_values[0];
                    if (variant.available !== false) {
                        const cleanedOptionName = cleanOptionName(option.bundle_option_name);
                        const handleizeName = handleize(cleanedOptionName);
                        const required_type = (option.is_required == 1) ? "required" : "";
                        
                        // Define which single-variant options should be visible
                        const visibleSingleVariantOptions = [
                            'Piston Set', 'Piston Ring Set', 'Main Bearing Set', 'Rod Bearing Set'
                        ];
                        
                        const shouldBeVisible = visibleSingleVariantOptions.some(visibleOption => 
                            cleanedOptionName.toLowerCase().includes(visibleOption.toLowerCase())
                        );
                        
                        const variantQty = (variant.qty != null && variant.qty !== '') ? String(variant.qty) : '1';
                        if (shouldBeVisible) {
                            // Create visible dropdown for important single-variant options
                            optionsHtml += `<div class="bold_option bold_option_dropdown ${handleizeName}"><label>`;
                            optionsHtml += `<span class="bold_option_title">${cleanedOptionName}</span>`;
                    const originalOptionName = option.bundle_option_original_name || option.bundle_option_name;
                    optionsHtml += `<span class="bold_option_element"><select id="dd_select_${parentProductId}_${b}" attr-name="${cleanedOptionName}" name="properties[${cleanedOptionName}]" data-original-name="${originalOptionName}" data-bundle-option-id="${option.bundle_option_id || ''}" class="bundle_option_select" ${required_type}>`;
                            optionsHtml += `<option value="">-- Choose ${cleanOptionName(option.bundle_option_name)} (Choose Oversize) --</option>`;
                            const selected = (variant.is_default == 1) ? 'selected' : '';
                            optionsHtml += `<option value="${variant.option_value}" data-option_value_key="0" data-variant-id="${variant.varient_id}" data-product-id="${variant.product_id}" data-price="${variant.price}" data-qty="${variantQty}" data-original-value="${variant.option_value}" ${selected}>${cleanOptionName(variant.option_value)}</option>`;
                            optionsHtml += `</select></span></label></div>`;
                        } else {
                            // Create hidden dropdown for other single-variant options
                            optionsHtml += `<div class="bold_option bold_option_dropdown" style="display: none;"><label>`;
                            optionsHtml += `<span class="bold_option_title">${cleanedOptionName}</span>`;
                            const originalOptionName = option.bundle_option_original_name || option.bundle_option_name;
                            optionsHtml += `<span class="bold_option_element"><select id="dd_select_${parentProductId}_${b}" style="display: none;" name="properties[${cleanedOptionName}]" data-original-name="${originalOptionName}" data-bundle-option-id="${option.bundle_option_id || ''}" class="bundle_option_select" ${required_type}>`;
                            optionsHtml += `<option value="">-- Choose ${cleanOptionName(option.bundle_option_name)} --</option>`;
                            optionsHtml += `<option value="${variant.option_value}" data-option_value_key="0" data-variant-id="${variant.varient_id}" data-product-id="${variant.product_id}" data-price="${variant.price}" data-qty="${variantQty}" data-original-value="${variant.option_value}" selected>${cleanOptionName(variant.option_value)}</option>`;
                            optionsHtml += `</select></span></label></div>`;
                            
                            // Add to initial attributes for hidden single-variant options
                            name_attributes.push(variant.option_value);
                            product_attributes.push(variant.product_id);
                            varient_attributes.push(variant.varient_id);
                            qty_attributes.push(variant.qty || '1');
                            shop_product_id += parseInt(variant.varient_id);
                            price_attributes.push(variant.price.toString().replace(".", ""));
                        }
                    }
                }
            });
        }

        const nameAttributesStr = name_attributes.length > 0 ? name_attributes.join(",").trim() : "";
        const productAttributesStr = product_attributes.length > 0 ? product_attributes.join(",").trim() : "";
        const varientAttributesStr = varient_attributes.length > 0 ? varient_attributes.join(",").trim() : "";
        const qtyAttributesStr = qty_attributes.length > 0 ? qty_attributes.join(",").trim() : "";
        const priceAttributesStr = price_attributes.length > 0 ? price_attributes.join(",").trim().replace(".", "") : "";

        option_set += optionsHtml;
        option_set += `<div class="bold_option_total">
            <input type="hidden" name="properties[_boldVariantNames]" data-property-name="_boldVariantNames" value="${nameAttributesStr}">
            <input type="hidden" name="properties[_boldVariantIds]" data-property-name="_boldVariantIds" value="${varientAttributesStr}">
            <input type="hidden" name="properties[_boldProductIds]" data-property-name="_boldProductIds" value="${productAttributesStr}">
            <input type="hidden" name="properties[_boldVariantPrices]" data-property-name="_boldVariantPrices" value="0">
            <input type="hidden" name="properties[_boldVariantQtys]" data-property-name="_boldVariantQtys" value="${qtyAttributesStr}">
            <input type="hidden" name="properties[_boldBuilderId]" data-property-name="_boldBuilderId" value="${shop_product_id}">
        </div></div>`;

        // Find the product form and inject bundle options
        const productForm = productItemElement.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");
        if (productForm) {
            // Check if bundle options already exist
            let existingBundleOptions = productForm.querySelector(".bold_option_set");
            
            if (!existingBundleOptions) {
                // Insert bundle options at the beginning of the form
                productForm.insertAdjacentHTML("afterbegin", option_set);
                
                // Get the newly inserted bundle options
                existingBundleOptions = productForm.querySelector(".bold_option_set");
            }
            
            // Ensure bundle options are hidden initially
            if (existingBundleOptions) {
                existingBundleOptions.classList.add("hide-bundle-choose");
            }

            // Enable buttons and set initial visibility
            const addToCartButton = productItemElement.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
            if (addToCartButton) {
                addToCartButton.removeAttribute("disabled");
                addToCartButton.classList.add("hide-bundle-choose");
            }
            
            const bundleChooseButton = productItemElement.querySelector("#bundle-choose");
            if (bundleChooseButton) {
                bundleChooseButton.removeAttribute("disabled");
                bundleChooseButton.classList.remove("hide-bundle-choose");
            }
        }
    },

    /**
     * Update bundle hidden fields when options change
     */
    updateBundleHiddenFields(productForm) {
        if (!productForm) return;

        const productItem = productForm.closest('[attr-prod-id]');
        if (!productItem) return;

        const parentBundleId = productItem.getAttribute("attr-prod-id");
        const bundleData = this.bundleCache.get(parentBundleId);

        if (!bundleData || !bundleData.bundle_options || !bundleData.bundle_options[0]) {
            // If no bundle data, try to read from existing hidden fields (for YMM bundles)
            return;
        }

        const optionsData = bundleData.bundle_options[0].options;
        let nameAttributes = [];
        let productAttributes = [];
        let varientAttributes = [];
        let qtyAttributes = [];
        let priceAttributes = [];
        let shopProductId = parseInt(parentBundleId);

        // Process all select elements (both visible and hidden)
        const allSelects = productForm.querySelectorAll(".bold_option_set select.bundle_option_select, .bold_option_element select");
        
        allSelects.forEach((selectElement) => {
            // Skip if select is hidden and has no visible options
            const parentDiv = selectElement.closest('.bold_option');
            const isHidden = parentDiv && parentDiv.style.display === 'none';
            
            const selectedOption = selectElement.options[selectElement.selectedIndex];
            
            if (selectedOption && selectedOption.value && selectedOption.value !== '' && !selectedOption.value.includes('-- Choose')) {
                let variantId = selectedOption.getAttribute('data-variant-id');
                let productId = selectedOption.getAttribute('data-product-id');
                let price = selectedOption.getAttribute('data-price');
                
                // If data attributes are missing, try to find variant from bundle data
                if (!variantId || !productId) {
                    const optionName = selectElement.getAttribute('attr-name') || 
                                     selectElement.getAttribute('name')?.replace('properties[', '').replace(']', '');
                    
                    if (optionName) {
                        const cleanedName = cleanOptionName(optionName);
                        const optionData = optionsData.find(opt => {
                            const optCleaned = cleanOptionName(opt.bundle_option_name);
                            return optCleaned.toLowerCase() === cleanedName.toLowerCase() ||
                                   opt.bundle_option_name.toLowerCase().includes(cleanedName.toLowerCase()) ||
                                   cleanedName.toLowerCase().includes(optCleaned.toLowerCase());
                        });
                        
                        if (optionData && optionData.options_values) {
                            // Find matching variant
                            const variant = optionData.options_values.find(v => {
                                const cleanValue = cleanOptionName(v.option_value);
                                const cleanOptValue = cleanOptionName(selectedOption.value);
                                return cleanValue === cleanOptValue || 
                                       selectedOption.value === v.option_value ||
                                       selectedOption.textContent.trim() === cleanValue;
                            });
                            
                            if (variant) {
                                variantId = variant.varient_id;
                                productId = variant.product_id;
                                price = variant.price || '0.00';
                                const qty = (variant.qty != null && variant.qty !== '') ? String(variant.qty) : '1';
                                selectedOption.setAttribute('data-variant-id', variantId);
                                selectedOption.setAttribute('data-product-id', productId);
                                selectedOption.setAttribute('data-price', price);
                                selectedOption.setAttribute('data-qty', qty);
                            }
                        }
                    }
                }
                
                if (variantId && productId) {
                    const qty = selectedOption.getAttribute('data-qty') || '1';
                    if (!isHidden || (isHidden && variantId)) {
                        nameAttributes.push(selectedOption.value);
                        productAttributes.push(productId);
                        varientAttributes.push(variantId);
                        qtyAttributes.push(qty);
                        shopProductId += parseInt(variantId);
                        priceAttributes.push(price ? price.toString().replace(".", "") : "0");
                    }
                }
            }
        });

        const nameAttributesStr = nameAttributes.length > 0 ? nameAttributes.join(",").trim() : "";
        const productAttributesStr = productAttributes.length > 0 ? productAttributes.join(",").trim() : "";
        const varientAttributesStr = varientAttributes.length > 0 ? varientAttributes.join(",").trim() : "";
        const qtyAttributesStr = qtyAttributes.length > 0 ? qtyAttributes.join(",").trim() : "";
        const priceAttributesStr = priceAttributes.length > 0 ? priceAttributes.join(",").trim().replace(".", "") : "";

        const boldOptionTotal = productForm.querySelector(".bold_option_total");
        if (boldOptionTotal) {
            const totalHtml = `
                <input type="hidden" name="properties[_boldVariantNames]" data-property-name="_boldVariantNames" value="${nameAttributesStr}">
                <input type="hidden" name="properties[_boldVariantIds]" data-property-name="_boldVariantIds" value="${varientAttributesStr}">
                <input type="hidden" name="properties[_boldProductIds]" data-property-name="_boldProductIds" value="${productAttributesStr}">
                <input type="hidden" name="properties[_boldVariantPrices]" data-property-name="_boldVariantPrices" value="0">
                <input type="hidden" name="properties[_boldVariantQtys]" data-property-name="_boldVariantQtys" value="${qtyAttributesStr}">
                <input type="hidden" name="properties[_boldBuilderId]" data-property-name="_boldBuilderId" value="${shopProductId}">
            `;
            boldOptionTotal.innerHTML = totalHtml;
        }
    },

    /**
     * Validate bundle options and clear errors
     */
    validateAndClearErrors(productForm) {
        if (!productForm) return;

        const bundleOptions = productForm.querySelector('.bold_option_set');
        if (!bundleOptions) return;

        const selects = bundleOptions.querySelectorAll('select.bundle_option_select');
        if (selects.length === 0) return;

        // Check if all required visible options are selected
        const visibleSelects = Array.from(selects).filter(select => {
            const parentDiv = select.closest('.bold_option');
            return parentDiv && parentDiv.style.display !== 'none';
        });
        
        const allSelected = visibleSelects.every(select => {
            const allOptions = Array.from(select.querySelectorAll('option'));
            const nonEmptyOptions = allOptions.filter(option => option.value.trim() !== '');
            
            if (nonEmptyOptions.length === 1) {
                if (select.value.trim() === '') {
                    select.value = nonEmptyOptions[0].value;
                }
                return true;
            }
            return select.value.trim() !== '';
        });

        if (allSelected) {
            const existingErrors = bundleOptions.querySelectorAll('.required-error');
            existingErrors.forEach(error => error.remove());
            
            visibleSelects.forEach(select => select.classList.remove('required-border'));
        } else {
            visibleSelects.forEach(select => {
                if (select.value.trim() === '') {
                    select.classList.add('required-border');
                } else {
                    select.classList.remove('required-border');
                }
            });
        }
    }
};

// Handle bundle choose button clicks
document.addEventListener("click", function (event) {
    if (event.target.matches("#bundle-choose") || event.target.closest("#bundle-choose")) {
        const bundleChooseBtn = event.target.matches("#bundle-choose") ? event.target : event.target.closest("#bundle-choose");
        if (!bundleChooseBtn) return;
        
        event.preventDefault();
        event.stopPropagation();
        
        const productItem = bundleChooseBtn.closest('[attr-prod-id]');
        if (!productItem) return;
        
        const form = productItem.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");
        if (!form) return;
        
        const productId = productItem.getAttribute("attr-prod-id");
        
        
        // Hide the choose button
        bundleChooseBtn.classList.add("hide-bundle-choose");
        
        // Check if bundle options exist
        let bundleOptions = form.querySelector(".bold_option_set");
        
        if (bundleOptions) {
            // Bundle options exist - show them
            bundleOptions.classList.remove("hide-bundle-choose");
            bundleOptions.querySelectorAll('.required-error.choose-size-first').forEach(el => el.remove());

            // If this is a metaobject bundle, ensure selects have the right classes
            const bundleData = window.catalogBundleData?.[productId];
            if (bundleData) {
                // Update existing options if needed
                const selects = bundleOptions.querySelectorAll('select');
                selects.forEach(select => {
                    if (!select.classList.contains('bundle_option_select')) {
                        select.classList.add('bundle_option_select');
                    }
                });
            }
        } else {
            // No bundle options found - check if we have metaobject data
            const bundleData = window.catalogBundleData?.[productId];
            
            if (bundleData) {
                window.catalogBundleManager.injectBundleOptions(productItem, bundleData, productId);
                bundleOptions = form.querySelector(".bold_option_set");
                if (bundleOptions) {
                    bundleOptions.classList.remove("hide-bundle-choose");
                }
            } else {
                console.warn(`[Catalog Bundle] No bundle data found for product ${productId}`);
            }
        }
        
        // Show add to cart button
        const addToCartBtn = productItem.querySelector(".bundle-product, .productitem--action-atc, .add-to-cart");
        if (addToCartBtn) {
            addToCartBtn.classList.remove("hide-bundle-choose");
        }
        
        // Update bundle hidden fields with current selections
        setTimeout(() => {
            if (form.querySelector(".bold_option_element select, .bold_option_set select")) {
                window.catalogBundleManager.updateBundleHiddenFields(form);
            }
        }, 100);
    }
});

// Handle bundle option changes
document.addEventListener("change", (event) => {
    // Check if this is a bundle option select (either with class or in bundle option set)
    const isBundleSelect = event.target.matches(".bundle_option_select") ||
                          event.target.closest(".bold_option_set")?.querySelector("select") === event.target;
    
    if (isBundleSelect) {
        event.stopPropagation();
        
        const productForm = event.target.closest("form");
        if (productForm) {
            const productItem = productForm.closest('[attr-prod-id]');
            const productId = productItem ? productItem.getAttribute("attr-prod-id") : null;
            
            // Check if this is a metaobject bundle
            const isMetaobjectBundle = productId && window.catalogBundleData && window.catalogBundleData[productId];
            
            if (isMetaobjectBundle) {
                const attrElement = event.target;
                const attrName = attrElement.getAttribute("attr-name");
                
                // Ensure this select has bundle_option_select class
                if (!attrElement.classList.contains('bundle_option_select')) {
                    attrElement.classList.add('bundle_option_select');
                }
                
                // Special handling for Piston Set -> Piston Ring Set (auto-suggestion)
                if (attrName === "Piston Set") {
                    let pistonSet = attrElement.value;
                    
                    if (pistonSet && pistonSet !== "") {
                        const pistonRingSetElement = productForm.querySelector('.bold_option_element select[attr-name="Piston Ring Set"], select[attr-name="Piston Ring Set"]');
                        if (pistonRingSetElement) {
                            let pistonRingSet = pistonSet.replace("Piston Set", "Piston Ring Set");
                            
                            const optionExists = Array.from(pistonRingSetElement.options).some(opt => opt.value === pistonRingSet);
                            if (optionExists) {
                                if (pistonRingSetElement.value !== pistonRingSet) {
                                    pistonRingSetElement.value = pistonRingSet;
                                    pistonRingSetElement.style.backgroundColor = '#f0f8ff';
                                    setTimeout(() => {
                                        pistonRingSetElement.style.backgroundColor = '';
                                    }, 2000);
                                    pistonRingSetElement.dispatchEvent(new Event('change', { bubbles: false }));
                                }
                            } else {
                                if (pistonRingSetElement.value !== "") {
                                    pistonRingSetElement.value = "";
                                    pistonRingSetElement.dispatchEvent(new Event('change', { bubbles: false }));
                                }
                            }
                        }
                    } else {
                        const pistonRingSetElement = productForm.querySelector('.bold_option_element select[attr-name="Piston Ring Set"], select[attr-name="Piston Ring Set"]');
                        if (pistonRingSetElement && pistonRingSetElement.value !== "") {
                            pistonRingSetElement.value = "";
                            pistonRingSetElement.dispatchEvent(new Event('change', { bubbles: false }));
                        }
                    }
                }
                
                window.catalogBundleManager.updateBundleHiddenFields(productForm);
                window.catalogBundleManager.validateAndClearErrors(productForm);
            } else {
                // Not a metaobject bundle - might be YMM bundle, let it handle normally
                return;
            }
        }
    }
});

// Handle add to cart for catalog metaobject bundles: validate visible options before allowing submit
document.addEventListener("click", function (event) {
    // Catalog page uses .add-to-cart, .bundle-product, #addToCart-product-template (not .productitem--action-atc)
    const addToCartBtn = event.target.closest(".add-to-cart.bundle-product, #addToCart-product-template, .product-grid-item .bundle-product, .product-grid-item .add-to-cart");
    if (!addToCartBtn) return;

    const productItem = addToCartBtn.closest('[attr-prod-id]');
    if (!productItem) return;

    const productId = productItem.getAttribute('attr-prod-id');
    if (!window.catalogBundleData || !window.catalogBundleData[productId]) {
        return; // Not a catalog metaobject bundle - let other handlers run
    }

    const form = productItem.querySelector("form[data-product-form], form.addToCartForm, form.js-product-form");
    if (!form) return;

    const bundleOptions = form.querySelector('.bold_option_set');
    if (!bundleOptions) return;

    // If bundle options are still hidden, user must click "Choose Size" first
    if (bundleOptions.classList.contains('hide-bundle-choose')) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const existingMsg = bundleOptions.querySelector('.required-error.choose-size-first');
        if (!existingMsg) {
            const msg = document.createElement('p');
            msg.classList.add('required-error', 'choose-size-first');
            msg.textContent = 'Please click Choose Size to select your options before adding to cart.';
            msg.style.color = 'red';
            msg.style.marginBottom = '10px';
            bundleOptions.insertBefore(msg, bundleOptions.firstChild);
        }
        return false;
    }

    // Bundle options are visible - validate that all visible selects have a value
    const selects = bundleOptions.querySelectorAll('select.bundle_option_select');
    const visibleSelects = Array.from(selects).filter(select => {
        const parentDiv = select.closest('.bold_option');
        const isHidden = parentDiv && (parentDiv.style.display === 'none' || (window.getComputedStyle && window.getComputedStyle(parentDiv).display === 'none'));
        return !isHidden;
    });

    const existingErrors = bundleOptions.querySelectorAll('.required-error');
    existingErrors.forEach(error => error.remove());

    let hasErrors = false;
    visibleSelects.forEach(select => {
        select.classList.remove('required-border');
        const nonEmptyOptions = Array.from(select.options).filter(opt => opt.value.trim() !== '');
        if (nonEmptyOptions.length > 0 && (select.value.trim() === '' || select.value.includes('-- Choose'))) {
            select.classList.add('required-border');
            hasErrors = true;
        }
    });

    if (hasErrors) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const errorMessage = document.createElement('p');
        errorMessage.classList.add('required-error');
        errorMessage.textContent = 'Please select all required options';
        errorMessage.style.color = 'red';
        errorMessage.style.marginBottom = '10px';
        bundleOptions.insertBefore(errorMessage, bundleOptions.firstChild);
        return false;
    }

    // Validation passed - allow the event to continue so empire.js add_to_cart_form_bind runs
}, true);

// Metaobject bundles use the theme's built-in add_to_cart_form_bind function from empire.js
// which handles bundle parent + child items and calls cart_update_count() for the flyout.
// We only need to ensure the hidden fields are populated correctly before the theme handles the click.
// The updateBundleHiddenFields function is called on option change to keep hidden fields updated.

